import { useState } from 'react'
import { Event } from '@/lib/calendar/types'
import { DroppableDayCell } from './DroppableDayCell'

interface MonthCalendarProps {
  selectedDate: Date
  onSelectDate: (date: Date) => void
  events: Event[]
  onMoveEvent: (eventId: string, newDate: Date) => void
  onEventClick: (event: Event) => void
  onViewPost?: (event: Event) => void
}

export function MonthCalendar({
  selectedDate,
  onSelectDate,
  events,
  onMoveEvent,
  onEventClick,
  onViewPost,
}: MonthCalendarProps) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const [_hoveredDate, _setHoveredDate] = useState<Date | null>(null)

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay()
  }

  const generateCalendarDays = () => {
    const currentMonth = selectedDate.getMonth()
    const currentYear = selectedDate.getFullYear()
    const daysInMonth = getDaysInMonth(currentMonth, currentYear)
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear)
    const days: (Date | null)[] = []

    // Add empty slots for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null)
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(currentYear, currentMonth, day))
    }

    return days
  }

  const isToday = (date: Date | null) => {
    if (!date) return false
    return date.toDateString() === today.toDateString()
  }

  const isSelected = (date: Date | null) => {
    if (!date) return false
    return date.toDateString() === selectedDate.toDateString()
  }

  const getEventsForDate = (date: Date | null) => {
    if (!date) return []
    return events.filter((event) => event.date.toDateString() === date.toDateString())
  }

  const calendarDays = generateCalendarDays()

  // Group days into rows of 7
  const calendarRows: (Date | null)[][] = []
  for (let i = 0; i < calendarDays.length; i += 7) {
    calendarRows.push(calendarDays.slice(i, i + 7))
  }

  return (
    <div className="flex h-full w-full flex-col">
      {/* Day headers */}
      <div className="mb-2 grid grid-cols-7">
        {dayNames.map((day) => (
          <div key={day} className="text-muted-foreground py-2 text-center text-sm">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid - each row is independent */}
      <div className="flex flex-1 flex-col gap-2">
        {calendarRows.map((row, rowIndex) => (
          <div key={rowIndex} className="grid flex-1 grid-cols-7 gap-2">
            {row.map((date, colIndex) => {
              if (!date) {
                return <div key={`${rowIndex}-${colIndex}`} />
              }

              return (
                <DroppableDayCell
                  key={`${rowIndex}-${colIndex}`}
                  date={date}
                  isToday={isToday(date)}
                  isSelected={isSelected(date)}
                  events={getEventsForDate(date)}
                  onSelectDate={onSelectDate}
                  onMoveEvent={onMoveEvent}
                  onEventClick={onEventClick}
                  onViewPost={onViewPost}
                />
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
